import { useRef, useState, useEffect } from "react";
import QRious from "qrious";

const ContactQRCode = () => {
  const [fields, setFields] = useState({
    first: "",
    last: "",
    org: "",
    title: "",
    phone: "",
    officePhone: "",
    email: "",
    email2: "",
    url: "",
    street: "",
    city: "",
    note: "",
  });
  const [size, setSize] = useState(300);
  const [qrData, setQrData] = useState("");
  const canvasRef = useRef(null);
  const previewRef = useRef(null);
  const downloadRef = useRef(null);
  const qr = useRef(null);

  // --- vCard text escaping ---
  const vcardEscape = (text) => {
    if (!text) return "";
    return String(text)
      .replace(/\\/g, "\\\\")
      .replace(/\n/g, "\\n")
      .replace(/;/g, "\\;")
      .replace(/,/g, "\\,");
  };

  // --- vCard builder ---
  const buildVCard = (f) => {
    const lines = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `N:${vcardEscape(f.last)};${vcardEscape(f.first)};;;`,
      `FN:${vcardEscape(f.first + (f.last ? " " + f.last : ""))}`,
    ];

    if (f.org) lines.push(`ORG:${vcardEscape(f.org)}`);
    if (f.title) lines.push(`TITLE:${vcardEscape(f.title)}`);
    if (f.phone) lines.push(`TEL;TYPE=CELL:${vcardEscape(f.phone)}`);
    if (f.officePhone)
      lines.push(`TEL;TYPE=WORK,VOICE:${vcardEscape(f.officePhone)}`);
    if (f.email) lines.push(`EMAIL;TYPE=INTERNET:${vcardEscape(f.email)}`);
    if (f.email2)
      lines.push(`EMAIL;TYPE=INTERNET,OTHER:${vcardEscape(f.email2)}`);
    if (f.url) lines.push(`URL:${vcardEscape(f.url)}`);

    if (f.street || f.city) {
      const adrStreet = vcardEscape(f.street || "");
      const cityParts = (f.city || "").split("/").map((p) => p.trim());
      const [city, region, postal, country] = [
        vcardEscape(cityParts[0] || ""),
        vcardEscape(cityParts[1] || ""),
        vcardEscape(cityParts[2] || ""),
        vcardEscape(cityParts[3] || ""),
      ];
      lines.push(`ADR:;;${adrStreet};${city};${region};${postal};${country}`);
    }

    if (f.note) lines.push(`NOTE:${vcardEscape(f.note)}`);
    lines.push("END:VCARD");

    return lines.join("\r\n");
  };

  // --- Generate QR and preview ---
  const generateQR = () => {
    const vcard = buildVCard(fields);
    setQrData(vcard);

    const qrSize = Math.max(120, Math.min(1200, Number(size) || 300));

    if (!qr.current) {
      qr.current = new QRious({
        element: canvasRef.current,
        size: qrSize,
        value: vcard,
      });
    } else {
      qr.current.size = qrSize;
      qr.current.value = vcard;
    }

    // update preview and download
    requestAnimationFrame(() => {
      const dataURL = canvasRef.current.toDataURL("image/png");
      previewRef.current.src = dataURL;
      downloadRef.current.href = dataURL;
    });
  };

  const clearForm = () => {
    setFields({
      first: "",
      last: "",
      org: "",
      title: "",
      phone: "",
      officePhone: "",
      email: "",
      email2: "",
      url: "",
      street: "",
      city: "",
      note: "",
    });
    setQrData("");
    if (qr.current) qr.current.value = "";
    if (previewRef.current) previewRef.current.src = "";
    if (downloadRef.current) downloadRef.current.href = "#";
    const ctx = canvasRef.current?.getContext("2d");
    if (ctx)
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };

  const handleChange = (e) => {
    setFields({ ...fields, [e.target.id]: e.target.value });
  };

  useEffect(() => {
    // sample data on load
    setFields({
      first: "John",
      last: "Doe",
      org: "Acme Inc.",
      title: "Engineer",
      phone: "+91 98765 43210",
      officePhone: "",
      email: "john.doe@example.com",
      email2: "",
      url: "",
      street: "123 Main St",
      city: "Mumbai / MH / 400001 / India",
      note: "Generated with Aeon",
    });
  }, []);

  useEffect(() => {
    if (fields.first) generateQR();
    // eslint-disable-next-line
  }, [fields, size]);

  return (
    <>
      <div class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
        <h1 class="h2">Contact QR (vCard) Generator</h1>
      </div>

      <div>
        <p>
          Enter contact info and click <strong>Generate QR</strong>. The QR
          encodes a vCard (Version 3.0) which most phones will import as a
          contact.
        </p>

        <div className="row">
          <div className="col-lg-6">
            <Label
              text="First name"
              id="first"
              value={fields.first}
              onChange={handleChange}
            />
            <Label
              text="Last name"
              id="last"
              value={fields.last}
              onChange={handleChange}
            />
            <Label
              text="Organization"
              id="org"
              value={fields.org}
              onChange={handleChange}
            />
            <Label
              text="Title"
              id="title"
              value={fields.title}
              onChange={handleChange}
            />
            <Label
              text="Phone"
              id="phone"
              value={fields.phone}
              onChange={handleChange}
            />
            <Label
              text="Office Phone"
              id="officePhone"
              value={fields.officePhone}
              onChange={handleChange}
            />
          </div>
          <div className="col-lg-6">
            <Label
              text="Email"
              id="email"
              value={fields.email}
              onChange={handleChange}
            />
            <Label
              text="Alternate Email"
              id="email2"
              value={fields.email2}
              onChange={handleChange}
            />
            <Label
              text="Website"
              id="url"
              value={fields.url}
              onChange={handleChange}
            />
            <Label
              text="Street address"
              id="street"
              value={fields.street}
              onChange={handleChange}
            />
            <Label
              text="City / Region / Postal / Country"
              id="city"
              value={fields.city}
              onChange={handleChange}
            />
            <Label
              text="Note"
              id="note"
              type="textarea"
              value={fields.note}
              onChange={handleChange}
            />
          </div>
        </div>

        <div style={styles.controls}>
          <label>
            QR size (px){" "}
            <input
              type="number"
              id="size"
              min="120"
              max="1200"
              value={size}
              onChange={(e) => setSize(e.target.value)}
              style={{ width: "120px" }}
            />
          </label>
          <div style={{ marginTop: 10 }}>
            <button onClick={generateQR}>Generate QR</button>{" "}
            <button onClick={clearForm}>Clear</button>
          </div>
        </div>

        <div style={styles.qrWrap}>
          <canvas ref={canvasRef} style={styles.qrCanvas}></canvas>

          <div>
            <img
              ref={previewRef}
              alt="QR preview"
              style={{
                display: "block",
                maxWidth: 220,
                border: "1px solid #eee",
                padding: 6,
                background: "#fff",
              }}
            />
            <div style={{ marginTop: 10 }}>
              <a ref={downloadRef} download="contact-qr.png" href="#">
                Download PNG
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// --- Small helper component for input fields ---
const Label = ({ text, id, value, onChange, type = "input" }) => (
  <label style={styles.label}>
    {text}
    {type === "textarea" ? (
      <textarea
        id={id}
        rows="2"
        value={value}
        onChange={onChange}
        style={styles.input}
      ></textarea>
    ) : (
      <input id={id} value={value} onChange={onChange} style={styles.input} />
    )}
  </label>
);

// --- CSS-in-JS styles ---
const styles = {
  label: {
    display: "block",
    marginTop: 10,
    fontWeight: 600,
  },
  input: {
    width: "100%",
    padding: 8,
    marginTop: 6,
    boxSizing: "border-box",
  },
  controls: {
    marginTop: 16,
  },
  qrWrap: {
    marginTop: 20,
    display: "flex",
    gap: 16,
    alignItems: "center",
  },
  qrCanvas: {
    border: "1px solid #ddd",
    padding: 8,
    background: "#fff",
  },
};

export default ContactQRCode;
